import { createFileRoute } from "@tanstack/react-router";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Avatar,
  Dialog,
  IconButton,
  Skeleton,
  Chip,
  Tooltip,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import {
  Download,
  PictureAsPdf,
  Image,
  InsertDriveFile,
  Description,
  Close,
  Visibility,
  Article,
  TableChart,
  Slideshow
} from "@mui/icons-material";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getGetProcessActivitiesHistoricQueryOptions
} from "../../../../../api/bpm/endpoints";
import {
  generateDocumentToken,
  findDocument
} from "../../../../../api/documentos/endpoints";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../auth";

interface AttachmentDto {
  uid: string;
  name: string;
  contentLength?: number;
  created: string;
  groupLabel?: string;
  groupId?: string;
  isDocument?: boolean;
  [key: string]: any;
}

export const Route = createFileRoute('/__auth/processes/$processInstanceId/history/documents')({
  component: DocumentsPage,
  staticData: {
    title: "routes.processDocuments",
  },
});

function FileIcon({ name }: { name: string }) {
  const ext = name.split('.').pop()?.toLowerCase();

  if (['pdf'].includes(ext || '')) return <PictureAsPdf color="error" />;
  if (['doc', 'docx'].includes(ext || '')) return <Article color="primary" />;
  if (['xls', 'xlsx', 'csv'].includes(ext || '')) return <TableChart color="success" />;
  if (['ppt', 'pptx'].includes(ext || '')) return <Slideshow color="warning" />;
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext || '')) return <Image color="secondary" />;

  return <InsertDriveFile color="action" />;
}

function DocumentCard({ doc, onView, onDownload }: { doc: AttachmentDto, onView: () => void, onDownload: () => void }) {
  const { t } = useTranslation();
  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownload();
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: 3,
          cursor: 'pointer'
        }
      }}
      onClick={onView}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar variant="rounded" sx={{ bgcolor: 'action.hover' }}>
            <FileIcon name={doc.name} />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={doc.name} placement="top">
              <Typography variant="subtitle2" noWrap>
                {doc.name}
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              {new Date(doc.created).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {doc.groupLabel && (
            <Chip label={doc.groupLabel} size="small" variant="outlined" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
          )}
          <Typography variant="caption" color="text.secondary">
            {doc.contentLength ? `${(doc.contentLength / 1024).toFixed(1)} KB` : ''}
          </Typography>
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end', borderTop: 1, borderColor: 'divider', px: 2, gap: 1 }}>
        <Button
          startIcon={<Visibility />}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          variant="outlined"
          sx={{ textTransform: 'none' }}
        >
          {t("common.view")}
        </Button>
        <Button
          startIcon={<Download />}
          size="small"
          onClick={handleDownload}
          variant="text"
          color="inherit"
          sx={{ textTransform: 'none' }}
        >
          {t("common.download")}
        </Button>
      </CardActions>
    </Card>
  );
}

function urlToBase64(url: string) {
  const utf8Bytes = new TextEncoder().encode(url);
  let binaryString = "";
  utf8Bytes.forEach((byte) => {
    binaryString += String.fromCharCode(byte);
  });
  return btoa(binaryString);
}

const DOCUMENT_GROUPS = [
  {
    id: 'registered',
    labelKey: 'documents.history.documents',
    labelFallback: 'Documentos',
    filter: (doc: AttachmentDto) => !!doc.fileRegisteredUid
  },
  {
    id: 'unregistered',
    labelKey: 'documents.history.unregisteredDocs',
    labelFallback: 'Documentos não registrados',
    filter: (doc: AttachmentDto) => !!doc.uid && !doc.fileRegisteredUid
  }
];

function DocumentsPage() {
  const { t } = useTranslation();
  const { processInstanceId } = Route.useParams();
  const { currentTenant } = useAuth();

  const [selectedDoc, setSelectedDoc] = useState<AttachmentDto | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'error'
  });

  const [selectedGroups, setSelectedGroups] = useState<string[]>(DOCUMENT_GROUPS.map(g => g.id));

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

  const getGroupLabel = (group: typeof DOCUMENT_GROUPS[number]) => t(group.labelKey, group.labelFallback);

  const { data: historyResponse, isLoading } = useQuery({
    ...getGetProcessActivitiesHistoricQueryOptions(processInstanceId, {
      firstResult: 0,
      maxResults: 500,
      sortBy: "startTime",
      sortOrder: "asc"
    }),
    enabled: !!processInstanceId
  });

  const variables = (historyResponse as any)?.variables || [];

  const allDocuments: (AttachmentDto & { groupLabel?: string, groupId?: string })[] = variables.reduce((acc: any[], item: any) => {
    try {
      if (!item.value) return acc;

      const parsedJson = JSON.parse(item.value);

      if (item.label === "ECMdocuments") {
        const parsedECMdocuments = parsedJson;
        if (Array.isArray(parsedECMdocuments)) {
          parsedECMdocuments.forEach((doc: any) => {
            if (doc.files) {
              const folderId = doc.id;
              doc.files.forEach((file: any) => {
                if (file.isMainFile !== true) return;
                if (file.isMinuta) return;

                let groupId = 'unregistered';
                if (file.id || file.fileRegisteredUid) groupId = 'registered';

                const uid = file.id || file.fileRegisteredUid;
                const constructedUrl = uid ? `${API_BASE_URL}/api/documentos/files/download/${uid}` : file.url;

                acc.push({
                  uid: uid,
                  name: file.title,
                  created: file.created || new Date().toISOString(),
                  contentLength: file.contentLength,
                  fileRegisteredUid: file.id,
                  folderId,
                  url: constructedUrl,
                  "Content-Type": file.mimetype || 'application/octet-stream',
                  groupLabel: getGroupLabel(DOCUMENT_GROUPS.find(g => g.id === groupId)!),
                  groupId,
                  isDocument: !!(doc.documentId != null || (doc.id != null && doc.files != null))
                });
              });
            }
          });
        }
      } else if (Array.isArray(parsedJson)) {
        parsedJson.forEach((doc) => {
          if (doc?.fileUid) {
            if (doc.isMinuta) return;
            let groupId = 'unregistered';
            if (doc.fileUid) groupId = 'registered';

            const constructedUrl = doc.fileUid ? `${API_BASE_URL}/api/documentos/files/download/${doc.fileUid}` : doc.url;

            acc.push({
              uid: doc.fileUid,
              name: doc.title,
              created: doc.created || new Date().toISOString(),
              url: constructedUrl,
              "Content-Type": doc.mimetype,
              ...doc,
              groupLabel: getGroupLabel(DOCUMENT_GROUPS.find(g => g.id === groupId)!),
              groupId,
              isDocument: !!(doc.documentId != null || (doc.id != null && doc.files != null))
            });
          } else if (doc) {
            Object.keys(doc).forEach((listItem) => {
              if (listItem) {
                try {
                  const docListItem = JSON.parse(doc[listItem]);
                  if (docListItem?.fileUid) {
                    if (docListItem.isMinuta) return;
                    let groupId = 'unregistered';
                    if (docListItem.fileRegisteredUid) groupId = 'registered';

                    const constructedUrl = docListItem.fileUid ? `${API_BASE_URL}/api/documentos/files/download/${docListItem.fileUid}` : docListItem.url;

                    acc.push({
                      uid: docListItem.fileUid,
                      name: docListItem.title,
                      created: docListItem.created || new Date().toISOString(),
                      url: constructedUrl,
                      "Content-Type": docListItem.mimetype,
                      ...docListItem,
                      groupLabel: getGroupLabel(DOCUMENT_GROUPS.find(g => g.id === groupId)!),
                      groupId,
                      isDocument: !!(docListItem.documentId != null || (docListItem.id != null && docListItem.files != null))
                    });
                  }
                } catch (err) { }
              }
            });
          }
        });
      } else if (parsedJson?.fileUid && !parsedJson.isMinuta) {
        let groupId = 'unregistered';
        if (parsedJson.fileRegisteredUid) groupId = 'registered';

        const constructedUrl = parsedJson.fileUid ? `${API_BASE_URL}/api/documentos/files/download/${parsedJson.fileUid}` : parsedJson.url;

        acc.push({
          uid: parsedJson.fileUid,
          name: parsedJson.title,
          created: parsedJson.created || new Date().toISOString(),
          url: constructedUrl,
          "Content-Type": parsedJson.mimetype,
          ...parsedJson,
          groupLabel: getGroupLabel(DOCUMENT_GROUPS.find(g => g.id === groupId)!),
          groupId,
          isDocument: !!(parsedJson.documentId != null || (parsedJson.id != null && parsedJson.files != null))
        });
      }
      return acc;
    } catch (e) {
      return acc;
    }
  }, []);

  const uniqueDocuments = Array.from(new Map(allDocuments.map(item => [item.uid, item])).values()).map(doc => {
    const group = DOCUMENT_GROUPS.find(g => g.filter(doc));
    return {
      ...doc,
      groupLabel: group ? getGroupLabel(group) : undefined,
      groupId: group?.id
    };
  });

  const filteredDocuments = uniqueDocuments.filter(doc => doc.groupId && selectedGroups.includes(doc.groupId));

  const handleToggleGroup = (groupId: string) => {
    setSelectedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const { mutateAsync: generateTempLink, isPending: isGeneratingLink } = useMutation({
    mutationFn: async (data: { fileUid: string }) => {
      const res = await generateDocumentToken(data.fileUid);
      return (res as any)?.data || res;
    },
  });

  const resolveFileUid = async (doc: AttachmentDto): Promise<string> => {
    if (doc.isDocument) {
      const documentId = doc.documentId || doc.id || doc.folderId;
      if (!documentId) {
        throw new Error(t("documents.idRequired") || "Identificador do documento não fornecido.");
      }

      const response = await findDocument(currentTenant, Number(documentId));
      const fetchedDoc = (response as any)?.data || response;
      if (!fetchedDoc || !fetchedDoc.files) {
        throw new Error(t("documents.fetchError") || "Não foi possível carregar o documento ou seus arquivos.");
      }
      const mainFile = fetchedDoc.files.find((f: any) => f.isMainFile === true);
      if (!mainFile) {
        throw new Error(t("documents.mainFileNotFound") || "Arquivo principal não encontrado.");
      }
      const ref = mainFile.externalReferences?.find((r: any) => r.documentUID);
      const resolvedUid = ref?.documentUID;
      if (!resolvedUid) {
        throw new Error(t("documents.uidNotFound") || "Identificador do arquivo principal não encontrado.");
      }
      return resolvedUid;
    } else {
      const fileUid = doc.fileUid || doc.uid;
      if (!fileUid) {
        throw new Error(t("documents.uidNotFound") || "Identificador de arquivo não encontrado.");
      }
      return fileUid;
    }
  };

  const handleViewDocument = async (doc: AttachmentDto) => {
    try {
      setResolving(true);
      const fileUid = await resolveFileUid(doc);
      if (fileUid) {
        const result = await generateTempLink({ fileUid });
        setSelectedDoc({ ...doc, url: result.url || doc.url || `${API_BASE_URL}/api/documentos/files/download/${fileUid}` });
        setViewerOpen(true);
      } else {
        throw new Error(t("documents.uidNotFound") || "Identificador de arquivo não encontrado.");
      }
    } catch (err: any) {
      console.error("Error viewing document:", err);
      const errorMessage = err.body?.message || err.message || t("documents.viewError") || "Erro ao visualizar o documento.";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setResolving(false);
    }
  };

  const handleDownloadDocument = async (doc: AttachmentDto) => {
    try {
      setResolving(true);
      const fileUid = await resolveFileUid(doc);
      if (fileUid) {
        const result = await generateTempLink({ fileUid });
        if (result.url) {
          window.open(result.url, '_blank');
        } else {
          window.open(doc.url || `${API_BASE_URL}/api/documentos/files/download/${fileUid}`, '_blank');
        }
      } else {
        throw new Error(t("documents.uidNotFound") || "Identificador de arquivo não encontrado.");
      }
    } catch (err: any) {
      console.error("Error downloading document:", err);
      const errorMessage = err.body?.message || err.message || t("documents.downloadError") || "Erro ao baixar o documento.";
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setResolving(false);
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedDoc(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {DOCUMENT_GROUPS.map(group => {
          const count = uniqueDocuments.filter(group.filter).length;
          const isSelected = selectedGroups.includes(group.id);
          return (
            <Chip
              key={group.id}
              label={`${getGroupLabel(group)} (${count})`}
              onClick={() => handleToggleGroup(group.id)}
              color={isSelected ? "primary" : "default"}
              variant={isSelected ? "filled" : "outlined"}
              clickable
            />
          );
        })}
      </Box>

      <Grid container spacing={2}>
        {filteredDocuments.map((doc, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={doc.uid || index}>
            <DocumentCard
              doc={doc}
              onView={() => handleViewDocument(doc)}
              onDownload={() => handleDownloadDocument(doc)}
            />
          </Grid>
        ))}
      </Grid>

      {filteredDocuments.length === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, opacity: 0.6 }}>
          <Description sx={{ fontSize: 60, mb: 2, color: 'text.disabled' }} />
          <Typography color="text.secondary">
            {uniqueDocuments.length > 0 ? t("processes.noDocThisFilter") : t("processes.noDocFound")}
          </Typography>
        </Box>
      )}

      <Dialog
        open={viewerOpen}
        onClose={handleCloseViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '85vh' }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" noWrap>{selectedDoc?.name}</Typography>
          <IconButton onClick={handleCloseViewer}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, bgcolor: '#f5f5f5', overflow: 'hidden', position: 'relative' }}>
          {selectedDoc && (
            selectedDoc.url ? (
              selectedDoc.name.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={`https://pdf-viewer.smartdocs.com.br/web/viewer.html?file=/pdf-proxy/${urlToBase64(selectedDoc.url)}`}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title={selectedDoc.name}
                />
              ) : (
                ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].some(ext => selectedDoc.name.toLowerCase().endsWith(ext)) ? (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    bgcolor: '#0e0e0e',
                    p: 2
                  }}>
                    <img
                      src={selectedDoc.url}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                      }}
                      alt={selectedDoc.name}
                    />
                  </Box>
                ) : (
                  <iframe
                    src={selectedDoc.url}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    title={selectedDoc.name}
                  />
                )
              )
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="error">{t("documents.urlNotAvailable")}</Typography>
              </Box>
            )
          )}
        </Box>
      </Dialog>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isGeneratingLink || resolving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
